import { apiBaseOrigin } from '../../../shared/config/api-origin';
import { createMonitoringRequest } from './http';
export type RegionalMode = 'LIVE' | 'DEMO' | 'HISTORICAL_RESEARCH';
export interface RegionalEvidence {
  id: string;
  kind: string;
  mode: RegionalMode;
  regionId: string;
  provider: string;
  product: string;
  sourceId: string;
  providerFamily: string;
  sourceRecordId: string;
  revision: string;
  evidenceDigest: string;
  geometryKind: string;
  geometry: number[][];
  observedAt: string | null;
  receivedAt: string | null;
  issuedAt: string | null;
  validFrom: string | null;
  validUntil: string | null;
  quality: string;
  endpoint: string;
  spatialGate: string;
  temporalGate: string;
  qualityGate: string;
  applicabilityGate: string;
  approvalGate: string;
  reasonCodes: string[];
  detail: Record<string, unknown>;
}
export interface RegionalAssessment {
  profile: {
    instanceId: string;
    agencyId: string;
    regionId: string;
    displayName: string;
    countryCode: string;
    timezone: string;
    revision: string;
    mode: RegionalMode;
    status: string;
    scenarioApplicability: string;
    geometryKind: string;
    geometrySource: string;
    spatialResolution: string;
    ruleOwner: string;
    administrationBoundary: string;
    fireAuthority: string;
  };
  asOf: string;
  evaluatedAt: string;
  revision: string;
  result: string;
  applicability: string;
  evidenceGrade: string;
  reasonCodes: string[];
  sourceLineages: string[];
  independentProviderCount: number;
  corroboration: string;
  correlationType: string;
  ruleId: string;
  ruleVersion: number;
  approvalState: string;
  evidence: RegionalEvidence[];
  explanation: string;
  auditRef: string;
}
export interface RegionalPage {
  sourceMode: RegionalMode;
  snapshotRevision: string;
  evaluatedAt: string;
  items: RegionalAssessment[];
  total: number;
  nextCursor: string | null;
  historicalCounts: Record<string, number>;
}
const object = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v);
export function parseRegional(v: unknown, mode: RegionalMode): RegionalPage {
  if (
    !object(v) ||
    v.sourceMode !== mode ||
    !Array.isArray(v.items) ||
    v.items.length > 4 ||
    typeof v.snapshotRevision !== 'string' ||
    !/^[a-f0-9]{64}$/.test(v.snapshotRevision) ||
    !Number.isSafeInteger(v.total)
  )
    throw new Error('REGIONAL_WIRE');
  for (const a of v.items) {
    if (
      !object(a) ||
      !object(a.profile) ||
      a.profile.mode !== mode ||
      typeof a.profile.regionId !== 'string' ||
      !Array.isArray(a.evidence) ||
      a.evidence.length > 50 ||
      !Array.isArray(a.reasonCodes) ||
      typeof a.revision !== 'string' ||
      !/^[a-f0-9]{64}$/.test(a.revision) ||
      !Number.isSafeInteger(a.independentProviderCount) ||
      (mode !== 'DEMO' &&
        (a.approvalState !== 'NOT_APPROVED' || a.result === 'WEATHER_CONDITION_ATTENTION'))
    )
      throw new Error('REGIONAL_SCOPE');
    for (const e of a.evidence) {
      if (
        !object(e) ||
        e.mode !== mode ||
        e.regionId !== a.profile.regionId ||
        typeof e.evidenceDigest !== 'string' ||
        !/^[a-f0-9]{64}$/.test(e.evidenceDigest) ||
        !Array.isArray(e.geometry) ||
        !Array.isArray(e.reasonCodes) ||
        !object(e.detail) ||
        ![
          'STATION_OBSERVATION',
          'GRID_WEATHER_FORECAST',
          'THERMAL_ANOMALY_CANDIDATE',
          'HISTORICAL_STATION_CONTEXT',
        ].includes(String(e.kind))
      )
        throw new Error('REGIONAL_EVIDENCE');
    }
  }
  return v as unknown as RegionalPage;
}
export const regionalUrl = (mode: RegionalMode, areaId = '', revision = '') => {
  const q = new URLSearchParams({ sourceMode: mode });
  if (areaId) q.set('areaId', areaId);
  if (revision) q.set('revision', revision);
  return apiBaseOrigin() + '/read-models/normal-monitoring/regional-evidence?' + q.toString();
};
export function readRegional(mode: RegionalMode, signal: AbortSignal) {
  return createMonitoringRequest({ baseOrigin: apiBaseOrigin() })(
    '/read-models/normal-monitoring/regional-evidence?sourceMode=' + mode,
    (v) => parseRegional(v, mode),
    signal,
  );
}
